import type {
  WorkspaceDeletionImpact,
  WorkspaceDeletionJobView,
  WorkspaceInvitationView,
  WorkspaceMemberView,
  WorkspaceMemberPreferences,
  WorkspaceView,
  WorkspaceRole,
  WorkspaceAccessPolicyView,
} from 'shared';
import { http } from './client';

export const workspaceApi = {
  list(status: 'active' | 'archived' = 'active'): Promise<WorkspaceView[]> {
    return http.get('/workspaces', { params: { status } });
  },
  get(workspaceId: string): Promise<WorkspaceView> {
    return http.get(`/workspaces/${workspaceId}`);
  },
  activate(workspaceId: string): Promise<{ ok: true }> {
    return http.post(`/workspaces/${workspaceId}/activate`);
  },
  create(input: {
    name: string;
    slug?: string;
    description?: string;
  }): Promise<WorkspaceView> {
    return http.post('/workspaces', input);
  },
  update(
    workspaceId: string,
    input: {
      settingsRevision: number;
      name?: string;
      description?: string | null;
    },
  ): Promise<WorkspaceView> {
    return http.patch(`/workspaces/${workspaceId}`, input);
  },
  archive(workspaceId: string): Promise<{ success: true }> {
    return http.post(`/workspaces/${workspaceId}/archive`);
  },
  restore(workspaceId: string): Promise<WorkspaceView> {
    return http.post(`/workspaces/${workspaceId}/restore`);
  },
  deletionImpact(workspaceId: string): Promise<WorkspaceDeletionImpact> {
    return http.get(`/workspaces/${workspaceId}/deletion-impact`);
  },
  permanentDelete(
    workspaceId: string,
    confirmationName: string,
  ): Promise<WorkspaceDeletionJobView> {
    return http.delete(`/workspaces/${workspaceId}`, { data: { confirmationName } });
  },
  deletionJob(jobId: string): Promise<WorkspaceDeletionJobView> {
    return http.get(`/workspaces/deletion-jobs/${jobId}`);
  },
  invitations(): Promise<WorkspaceInvitationView[]> {
    return http.get('/workspaces/invitations');
  },
  members(workspaceId: string): Promise<WorkspaceMemberView[]> {
    return http.get(`/workspaces/${workspaceId}/members`);
  },
  roles(workspaceId: string): Promise<WorkspaceRole[]> {
    return http.get(`/workspaces/${workspaceId}/roles`);
  },
  accessPolicy(workspaceId: string): Promise<WorkspaceAccessPolicyView> {
    return http.get(`/workspaces/${workspaceId}/access-policy`);
  },
  saveAccessPolicy(workspaceId: string, revision: number, roles: WorkspaceRole[]): Promise<WorkspaceAccessPolicyView> {
    return http.put(`/workspaces/${workspaceId}/access-policy`, { revision, roles });
  },
  updateMyPreferences(
    workspaceId: string,
    input: Partial<WorkspaceMemberPreferences>,
  ): Promise<WorkspaceMemberView> {
    return http.patch(`/workspaces/${workspaceId}/members/me/preferences`, input);
  },
  inviteMember(
    workspaceId: string,
    input: { email: string; role: string },
  ): Promise<WorkspaceMemberView> {
    return http.post(`/workspaces/${workspaceId}/members/invitations`, input);
  },
  acceptInvitation(workspaceId: string): Promise<WorkspaceMemberView> {
    return http.post(`/workspaces/${workspaceId}/members/invitations/accept`);
  },
  rejectInvitation(workspaceId: string): Promise<{ success: true }> {
    return http.post(`/workspaces/${workspaceId}/members/invitations/reject`);
  },
  updateMember(
    workspaceId: string,
    memberId: string,
    input: {
      role: string;
      creditLimit?: number | null;
    },
  ): Promise<WorkspaceMemberView> {
    return http.patch(`/workspaces/${workspaceId}/members/${memberId}`, input);
  },
  removeMember(workspaceId: string, memberId: string): Promise<{ success: true }> {
    return http.delete(`/workspaces/${workspaceId}/members/${memberId}`);
  },
  leave(workspaceId: string): Promise<{ success: true }> {
    return http.post(`/workspaces/${workspaceId}/members/leave`);
  },
};
